import { action, computed, makeObservable, observable } from 'mobx';
import { DeviceDetailEntryStore, IDeviceDetailsConstructorOptions } from './device-detail-entry-store';
import {
    addOffsetToTheDate,
    addTzToDate,
    DEFAULT_HISTORICAL_RANGE,
    setBeginningOfTheDay,
    setEndOfTheDay,
} from '../../common-utils/date-utils';


export abstract class HistoricalListingStore<T> extends DeviceDetailEntryStore<T> {
    constructor(name: string, options: IDeviceDetailsConstructorOptions) {
        super(name, options);
        makeObservable(this);
    }
    private static MAX_RETRIES_WHEN_SERVICE_UNAVAILABLE = 2;
    
    @observable
    private _filterByDateFrom: Date;

    get filterByDateFrom(): Date {
        return setBeginningOfTheDay(
            this._filterByDateFrom
            || addOffsetToTheDate(new Date(), -DEFAULT_HISTORICAL_RANGE + 1)
        );
    }

    @observable
    private _filterByDateTo: Date;

    get filterByDateTo(): Date {
        return setEndOfTheDay(this._filterByDateTo || new Date());
    }

    @action
    setFilterByDateFrom(filterByDateFrom: Date) {
        this._filterByDateFrom = filterByDateFrom;
    }

    @action
    setFilterByDateTo(filterByDateTo: Date) {
        this._filterByDateTo = filterByDateTo;
    }

    @computed
    get searchDateRange(): [string, string] {
        return [addTzToDate(this.filterByDateFrom).toISOString(), addTzToDate(this.filterByDateTo).toISOString()];
    }

    protected abstract getHistoricalEntries(accountId: number, uid: string, filterByDateFrom: string, filterByDateTo: string): Promise<T[]>;
    protected async initializeData(retryTimes = 0): Promise<any[]> {
            if (retryTimes > HistoricalListingStore.MAX_RETRIES_WHEN_SERVICE_UNAVAILABLE) {
                throw new Error('Service is unavailable at the moment. Please try to refresh the page later.');
            }
            try {
                const [filterByDateFrom, filterByDateTo] = this.searchDateRange;
                return await this.getHistoricalEntries(this.accountId, this.uid, filterByDateFrom, filterByDateTo);
            } catch (error) {
                if (typeof error !== 'string' && error?.message.toLowerCase().includes('service unavailable')) {
                    return this.initializeData(++retryTimes);
                }
                throw error;
            }
        }
}
